"use client";

import { DEMO_CALENDAR_LINK } from "@/lib/constants/booking";
import LogoWall from "@/app/(public)/shared/components/LogoWall";
import Testimonial from "@/app/(public)/shared/components/Testimonial";
import { UnifiedModal, useModalManager } from "@/app/(public)/shared/components/UnifiedModal";
import { Button } from "@/app/(public)/shared/ui/Button";
import Image from "next/image";
import Link from "next/link";
import easGraphic from "@/app/(public)/shared/assets/graphics/eas.png";
import { CheckCircle } from "lucide-react";

export default function ExecutiveAssistants() {
  const { openModal, closeModal, isModalOpen } = useModalManager();

  return (
    <main>
      {/* Hero Section */}
      <section className="min-h-[calc(100vh-64px)] lg:h-[calc(100vh-64px)] py-8 mt-[64px]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-16 h-full">
          <div className="flex flex-col justify-center gap-8 md:gap-16 lg:gap-24">
            <div className="flex flex-col gap-6">
              <h1 className="text-h1 text-landing-primary">Your secret weapon for unforgettable offsites.</h1>
              <p className="text-body-lg text-landing-secondary max-w-530">
                We work with the best EAs in the business - proudly partnered with eaMAFIA.
              </p>
              <div className="flex gap-4">
                <Button variant="primary" color="primary" size="md" onClick={() => openModal("demo")}>
                  Get Started
                </Button>
                <Link href="/executive-assistants/one-pager" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" color="neutral" size="md">
                    View One-Pager
                  </Button>
                </Link>
              </div>
            </div>

            {/* Static Logo Wall */}
            <LogoWall
              variant="small-static"
              type="enterprise-small"
              trustedByText="Trusted by world class companies"
              className="justify-start"
            />
          </div>
          <div className="flex items-center justify-center lg:h-full mt-8 lg:mt-0">
            <div className="w-full max-w-lg lg:max-w-none">
              <Image
                src={easGraphic}
                alt="Executive assistants solution illustration"
                className="w-full h-auto object-contain rounded-2xl"
                priority
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto">
          {/* Section Header */}
          <div className="mb-16">
            <h2 className="text-h2 text-landing-primary mb-6">
              Impress your exec. Delight your team. Reclaim your time.
            </h2>
            <p className="text-body-lg text-landing-secondary max-w-2xl">
              Everything you need to manage executive events and travel with precision and efficiency.
            </p>
          </div>

          {/* Key Features - converted to document style */}
          <div className="space-y-12 mb-20">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">
                  Send a single brief, get venues, quotes, and flights in days
                </h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Streamline your workflow with one comprehensive brief that delivers everything you need. Our integrated
                approach eliminates the need to coordinate with multiple vendors, saving you hours of back-and-forth
                communication.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Personalized support for every event</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Work with dedicated specialists who understand executive-level expectations. Our team anticipates needs,
                proactively addresses challenges, and ensures every detail meets the high standards your executives
                expect.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Tools to track budgets, timelines, and approvals</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Stay on top of every detail with comprehensive project management tools. Monitor spending, track
                deadlines, and manage approval workflows all in one place, giving you complete visibility and control
                over your events.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">
                  One contract, one point of contact—zero vendor wrangling
                </h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Simplify your vendor management with a single trusted partner for all your needs. One invoice, one
                relationship, one point of accountability - freeing you to focus on what matters most: supporting your
                executive team.
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Executive calendar and travel coordination</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Manage complex executive schedules with intelligent calendar integration, travel booking automation, and
                real-time updates that keep everything running smoothly. Coordinate multiple time zones, priorities, and
                travel requirements with precision that exceeds expectations.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">High-touch event management</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Coordinate VIP events, board meetings, and executive retreats with attention to detail that matches the
                highest standards of executive support. Ensure flawless execution of critical gatherings where every
                detail matters and outcomes drive business success.
              </p>
            </div>

            <div>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="w-6 h-6 text-primary" />
                <h3 className="text-h3 text-landing-primary">Expense tracking and reporting</h3>
              </div>
              <p className="text-body-lg text-landing-secondary leading-relaxed">
                Automate expense management with integrated tracking, approval workflows, and detailed reporting that
                keeps executives informed and compliant. Maintain transparency and accountability across all
                event-related expenses with comprehensive audit trails.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-12 sm:py-0">
        <div className="">
          <Testimonial variant="single" person="janet" title="Don't just take our word for it." />
        </div>
      </section>

      {/* Demo Modal */}
      <UnifiedModal
        type="cal"
        isOpen={isModalOpen("demo")}
        onClose={() => closeModal("demo")}
        title="Executive Assistant Solutions"
        subtitle="Schedule a call to learn how we can make you indispensable"
        size="lg"
        calLink={DEMO_CALENDAR_LINK}
        fallbackText="Book your executive assistant demo"
      />
    </main>
  );
}
